import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../../config/supabase-clean';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

/**
 * ENTERPRISE AUTH CONTEXT
 * 
 * Key Features:
 * 1. Query Deduplication - Prevents duplicate database calls
 * 2. Session Caching - Smart localStorage with TTL
 * 3. Optimistic Loading - Show cached data instantly, refresh in background
 * 4. No Blocking Timeouts - Async background refresh instead
 * 5. Single Initialization - Only runs once per session
 */
export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Refs for controlling execution flow
  const initializedRef = useRef(false);
  const profileLoadPromiseRef = useRef(null);
  const lastProfileLoadRef = useRef(0);

  // Cache configuration
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache validity
  const MIN_REFRESH_INTERVAL = 1000; // Don't refresh more than once per second

  /**
   * Load cached auth data from localStorage
   * Returns null if cache is invalid or expired
   */
  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem('admin_auth_cache');
      if (!cached) return null;

      const { adminUser, organization, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      // Check if cache is still valid
      if (age > CACHE_TTL) {
        console.log('🗑️ Cache expired, clearing...');
        localStorage.removeItem('admin_auth_cache');
        return null;
      }

      console.log(`✅ Loaded from cache (age: ${Math.round(age / 1000)}s)`);
      return { adminUser, organization };
    } catch (error) {
      console.error('❌ Cache read error:', error);
      localStorage.removeItem('admin_auth_cache');
      return null;
    }
  };

  /**
   * Save auth data to localStorage with timestamp
   */
  const saveToCache = (adminUser, organization) => {
    try {
      const cacheData = {
        adminUser,
        organization,
        timestamp: Date.now()
      };
      localStorage.setItem('admin_auth_cache', JSON.stringify(cacheData));
      console.log('💾 Cached auth data');
    } catch (error) {
      console.error('❌ Cache write error:', error);
    }
  };

  /**
   * Clear all cached auth data
   */
  const clearCache = () => {
    localStorage.removeItem('admin_auth_cache');
    console.log('🗑️ Cache cleared');
  };

  /**
   * Load admin profile with query deduplication
   * If already loading, returns the existing promise
   */
  const loadAdminProfile = async (userId, forceRefresh = false) => {
    // Prevent duplicate calls within MIN_REFRESH_INTERVAL
    const timeSinceLastLoad = Date.now() - lastProfileLoadRef.current;
    if (!forceRefresh && timeSinceLastLoad < MIN_REFRESH_INTERVAL) {
      console.log('⏭️ Skipping profile load (too soon since last load)');
      return profileLoadPromiseRef.current;
    }

    // If already loading, return the existing promise (deduplication)
    if (profileLoadPromiseRef.current && !forceRefresh) {
      console.log('♻️ Reusing existing profile load promise');
      return profileLoadPromiseRef.current;
    }

    // Create new load promise
    lastProfileLoadRef.current = Date.now();
    profileLoadPromiseRef.current = (async () => {
      console.log('🔄 Loading admin profile for user:', userId);

      try {
        // Query admin_users table
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('auth_user_id', userId)
          .single();

        if (adminError) {
          console.error('❌ Admin user query error:', adminError);
          throw new Error(`Admin user not found: ${adminError.message}`);
        }

        if (!adminData) {
          throw new Error('Admin user not found');
        }

        console.log('✅ Admin user loaded:', adminData.email);

        // Query organizations table
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', adminData.organization_id)
          .single();

        if (orgError) {
          console.error('❌ Organization query error:', orgError);
          throw new Error(`Organization not found: ${orgError.message}`);
        }

        if (!orgData) {
          throw new Error('Organization not found');
        }

        console.log('✅ Organization loaded:', orgData.name);

        // Update state
        setAdminUser(adminData);
        setOrganization(orgData);

        // Save to cache
        saveToCache(adminData, orgData);

        console.log('✅ Admin profile loaded successfully');
        return { adminUser: adminData, organization: orgData };

      } catch (error) {
        console.error('❌ Error loading admin profile:', error);
        
        // On error, try to use cache as fallback
        const cached = loadFromCache();
        if (cached && !adminUser && !organization) {
          console.log('⚠️ Using cached data as fallback after error');
          setAdminUser(cached.adminUser);
          setOrganization(cached.organization);
        } else {
          // If no cache available and this is a critical error, sign out
          if (!error.message?.includes('not found')) {
            console.log('🔓 Signing out due to critical error');
            await supabase.auth.signOut();
          }
        }
        
        throw error;
      } finally {
        profileLoadPromiseRef.current = null;
      }
    })();

    return profileLoadPromiseRef.current;
  };

  /**
   * Initialize auth state on mount
   * Only runs once per app lifecycle
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current) {
      console.log('⏭️ Auth already initialized, skipping');
      return;
    }

    initializedRef.current = true;

    const initializeAuth = async () => {
      console.log('=== INITIALIZING AUTH (Enterprise Mode) ===');

      try {
        // Try to restore from cache immediately for optimistic UI
        const cached = loadFromCache();
        if (cached) {
          console.log('⚡ Optimistic: Using cached auth data');
          setAdminUser(cached.adminUser);
          setOrganization(cached.organization);
          setLoading(false); // Set loading to false immediately
        }

        // Check current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setAdminUser(null);
          setOrganization(null);
          clearCache();
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Valid session found');

          // If we have cache, refresh in background
          if (cached) {
            console.log('🔄 Refreshing profile in background...');
            loadAdminProfile(session.user.id, false).catch(err => {
              console.error('Background refresh failed:', err);
            });
          } else {
            // No cache, load profile (this will set loading to false when done)
            await loadAdminProfile(session.user.id, false);
          }
        } else {
          console.log('ℹ️ No session found');
          setAdminUser(null);
          setOrganization(null);
          clearCache();
        }

      } catch (error) {
        console.error('❌ Initialization error:', error);
      } finally {
        // Only set loading to false if we haven't already (from cache)
        setLoading(false);
        console.log('=== AUTH INITIALIZED ===');
      }
    };

    initializeAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔔 Auth event:', event);

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('🔓 User signed in');

        // Skip if we've already initialized (prevents duplicate loads)
        if (initializedRef.current) {
          console.log('⏭️  Skipping profile load (already initialized)');
          return;
        }

        console.log('📥 Loading admin profile (fresh login)');
        await loadAdminProfile(session.user.id, false);
        
      } else if (event === 'SIGNED_OUT') {
        console.log('🔒 User signed out');
        setAdminUser(null);
        setOrganization(null);
        clearCache();
        
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed');
        // Optionally refresh profile data on token refresh
        if (session?.user) {
          loadAdminProfile(session.user.id, false).catch(err => {
            console.error('Profile refresh after token refresh failed:', err);
          });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once

  /**
   * Login function - authenticates and loads profile
   */
  const login = async (email, password, currentDomain) => {
    try {
      console.log('🔐 Login attempt for:', email);

      // Step 1: Verify organization by domain
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('custom_domain', currentDomain)
        .single();

      if (orgError || !orgData) {
        console.error('❌ Organization not found for domain:', currentDomain);
        return { success: false, error: 'Organization not found for this domain' };
      }

      console.log('✅ Organization verified:', orgData.name);

      // Step 2: Verify admin user exists and is active
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', email)
        .eq('organization_id', orgData.id)
        .eq('is_active', true)
        .single();

      if (adminError || !adminData) {
        console.error('❌ Admin user not found or inactive');
        return { success: false, error: 'Invalid credentials or account inactive' };
      }

      console.log('✅ Admin user verified');

      // Step 3: Authenticate with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        console.error('❌ Authentication failed:', authError);
        return { success: false, error: authError.message };
      }

      console.log('✅ Authentication successful');

      // Step 4: Load profile (this will trigger via onAuthStateChange, but we'll do it explicitly for immediate feedback)
      if (authData.session?.user) {
        await loadAdminProfile(authData.session.user.id, true);
      }

      return { success: true };

    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  /**
   * Logout function - clears session and cache
   */
  const logout = async () => {
    console.log('🔓 Logging out...');

    try {
      // Clear local state first for immediate UI update
      setAdminUser(null);
      setOrganization(null);
      clearCache();

      // Then sign out from Supabase
      await supabase.auth.signOut({ scope: 'global' });
      
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  // Computed value for authentication status
  const isAuthenticated = !!adminUser && !!organization;

  const value = {
    adminUser,
    organization,
    loading,
    login,
    logout,
    isAuthenticated,
    refreshProfile: () => {
      const session = supabase.auth.getSession();
      if (session) {
        return loadAdminProfile(session.user.id, true);
      }
    }
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;